import { Injectable } from '@angular/core';
import { AjaxResponse } from 'rxjs/ajax';
import { Observable, from, of } from 'rxjs';
import { concatMap, map, reduce } from 'rxjs/operators';
import { chunk as _chunk } from 'lodash';
import { environment } from '../../../../environments/environment';
import { BaseRequester } from '../common/base-requester';
import { EntityId, StoreId } from '../../config/store-config';
import { FinanceOrderRecord, PeriodBasis } from './finance-record';

// Client for the finance-tx Lambda: the store of normalized marketplace
// financial records. Follows the OutputTaxService shape — POST an
// {operation, parameter} envelope to one endpoint, unwrap `errorMessage`.

export interface FinancePeriodQuery {
    periodBasis: PeriodBasis;
    // Epoch seconds, inclusive at both ends.
    from: number;
    to: number;
    entityIds?: EntityId[];
    storeIds?: StoreId[];
}

export interface FinanceUpsertResult {
    matched: number;
    modified: number;
    inserted: number;
    // Records whose stored taxpayer disagrees with the one just resolved.
    // Never applied automatically — see the note on the upsert method.
    entityDrift: { _id: string; stored: EntityId; incoming: EntityId }[];
}

export interface FinanceEntityRollup {
    _id: EntityId;
    orders: number;
    // Stored documents. Equal to `orders` except where one order is stored
    // as several settlement parts. Absent from a Lambda older than
    // 2026-09-13, which counted documents as `orders`.
    rows?: number;
    buyerTotal: number;
    // The VAT base (FinanceGross.taxableConsideration). Absent from a Lambda
    // older than 2026-09-13; fall back to buyerTotal.
    taxableConsideration?: number;
    sellerRevenue: number;
    sellerDiscount: number;
    platformSubsidy: number;
    feeCommission: number;
    feeTransaction: number;
    feeService: number;
    feeAffiliate: number;
    feeShipping: number;
    feeOther: number;
    feeTotal: number;
    netSettlement: number;
    vatBase: number;
    vatOutput: number;
    feeVatInput: number;
    // Documents in the period with no tax point / no order date. What the
    // basis-gap banner reports. Absent from a Lambda older than 2026-09-13.
    nullTaxPoint?: number;
    nullOrderDate?: number;
    // Money refunded to buyers (positive) and the output VAT inside it on
    // VAT-bearing rows, unrounded. Absent from a Lambda older than
    // 2026-09-13.
    refunds?: number;
    refundVatOutput?: number;
}

export type FinanceStoreRollup = Omit<FinanceEntityRollup, '_id'> & {
    _id: StoreId;
};

export interface FinanceSummary {
    byEntity: FinanceEntityRollup[];
    byStore: FinanceStoreRollup[];
    // How much of the total came from stores whose fees are only partly
    // itemized, so a blended fee rate is never read as exact.
    byDetailLevel: {
        _id: string;
        orders: number;
        feeTotal: number;
        feeOther: number;
    }[];
    // Present only when thresholdYear was requested.
    calendarYearByEntity?: {
        _id: EntityId;
        orders: number;
        buyerTotal: number;
        taxableConsideration?: number;
        refunds?: number;
        sellerRevenue: number;
        // The same figures split by Bangkok-local calendar month (YYYY-MM),
        // in no particular order. Absent from a Lambda older than
        // 2026-09-02 — the two deploy independently — and the projection
        // then falls back to pro rata by days elapsed.
        months?: {
            month: string;
            orders: number;
            buyerTotal: number;
            taxableConsideration?: number;
            refunds?: number;
        }[];
    }[];
    thresholdYear?: number;
}

@Injectable({
    providedIn: 'root',
})
export class FinanceTxService extends BaseRequester {
    // A month across five stores is a few thousand records. Sent in chunks so
    // no single call approaches the gateway's 29s integration timeout, and so
    // a failure costs one chunk rather than the whole sync.
    private static readonly UPSERT_CHUNK_SIZE = 500;

    constructor() {
        super();
    }

    // Idempotent: records are keyed on `_id`, so re-syncing a period updates in
    // place. `entityId` is written once and never overwritten, so a later
    // change of a store's owner in config cannot restamp an old month with
    // today's owner. Any disagreement comes back in `entityDrift` — surface
    // it, do not apply it.
    public upsert(
        records: FinanceOrderRecord[],
    ): Observable<FinanceUpsertResult> {
        if (records.length === 0) {
            return of({
                matched: 0,
                modified: 0,
                inserted: 0,
                entityDrift: [],
            });
        }
        const chunks: FinanceOrderRecord[][] = _chunk(
            records,
            FinanceTxService.UPSERT_CHUNK_SIZE,
        );
        return from(chunks).pipe(
            // concatMap rather than mergeMap: chunks of one period touch the
            // same collection and there is nothing to gain from racing them.
            concatMap((batch: FinanceOrderRecord[]) =>
                this.request<FinanceUpsertResult>({
                    operation: 'upsert',
                    parameter: { records: batch },
                }),
            ),
            reduce(
                (acc: FinanceUpsertResult, part: FinanceUpsertResult) => ({
                    matched: acc.matched + (part.matched ?? 0),
                    modified: acc.modified + (part.modified ?? 0),
                    inserted: acc.inserted + (part.inserted ?? 0),
                    entityDrift: [
                        ...acc.entityDrift,
                        ...(part.entityDrift ?? []),
                    ],
                }),
                { matched: 0, modified: 0, inserted: 0, entityDrift: [] },
            ),
        );
    }

    public query(query: FinancePeriodQuery): Observable<FinanceOrderRecord[]> {
        return this.request<FinanceOrderRecord[]>({
            operation: 'query',
            parameter: query,
        });
    }

    // `thresholdYear` asks for calendar-year revenue per taxpayer alongside the
    // period rollups. It deliberately ignores the period filter — the ฿1.8M VAT
    // threshold is a calendar-year test, so answering it from the window the
    // user happens to be viewing would understate it.
    public summary(
        query: FinancePeriodQuery,
        thresholdYear?: number,
    ): Observable<FinanceSummary> {
        return this.request<FinanceSummary>({
            operation: 'summary',
            parameter: { ...query, thresholdYear },
        });
    }

    private request<T>(payload: unknown): Observable<T> {
        return this.internalPostRequest(
            environment.financeTxEndpoint,
            payload,
        ).pipe(
            map((response: AjaxResponse) => {
                if (response.response?.errorMessage) {
                    throw new Error(
                        response.response.errorMessage || response.response,
                    );
                }
                return response.response as T;
            }),
        );
    }
}
